import React from 'react';

import './styles.scss';
import classNames from 'classnames';
import {isIOS, isMacOs, isIPad13, isTablet} from 'react-device-detect';

import playPause from 'utils/playPause';
import WebAudioVoice from 'utils/audioEngine';

import getUniqueId from 'utils/getUniqueId';

import {DASHARRAY_LENGTH} from 'constants/index';

import 'actions';
import strings from 'utils/strings';

const isLikelyIPad = () => {
	if (typeof navigator === 'undefined') return false;
	const ua = navigator.userAgent || '';
	const maxTouchPoints = navigator.maxTouchPoints || 0;
	return (
		(isMacOs && maxTouchPoints > 1) ||
		/iPad/i.test(ua) ||
		Boolean(isIPad13) ||
		(isIOS && isTablet)
	);
};

class AudioPlayer extends React.Component {
	constructor() {
		super();

		this.uniqueId = `audio${getUniqueId()}`;

		this.state = {
			isPlaying: false,
			duration: 0, // ms
			currentTime: 0,
			isDone: false,
			isStarted: false,
		};
		this.isAutoplayRetryArmed = false;

		this.tick = this.tick.bind(this);
		this.handleStartPlayer = this.handleStartPlayer.bind(this);
		this.handlePause = this.handlePause.bind(this);
		this.handlePlay = this.handlePlay.bind(this);
		this.handleDurationChange = this.handleDurationChange.bind(this);
		this.retryAutoplayOnGesture = this.retryAutoplayOnGesture.bind(this);
	}

	componentDidMount() {
		this.createVoice(this.props.src);
		this.startAutoplay();
	}

	componentWillReceiveProps(nextProps) {
		const {src} = this.props;
		if (src && src !== nextProps.src) {
			this.handlePause();
		}
	}

	componentDidUpdate(prevProps) {
		const {src} = this.props;
		if (prevProps.src && src !== prevProps.src) {
			this.disarmAutoplayRetry();
			this.detachVoiceListeners(this.voice);
			this.voice.destroy();
			this.createVoice(src);
			this.startAutoplay();
		}
	}

	componentWillUnmount() {
		cancelAnimationFrame(this.animationFrame);
		this.disarmAutoplayRetry();
		this.detachVoiceListeners(this.voice);
		this.voice.destroy();
		this.setState({
			isDone: true,
		});
	}

	getId() {
		return this.props.id || this.uniqueId;
	}

	createVoice(src) {
		this.voice = new WebAudioVoice(src);
		this.attachVoiceListeners(this.voice);
		// Kick decoding off immediately (mirrors a native <audio> element's
		// implicit metadata preload) so playback starts without delay once
		// play() is actually called.
		this.voice.load().catch(() => {});

		const {refs} = this.props;
		const id = this.getId();
		if (refs && id) {
			refs[id] = this.voice;
		}
	}

	attachVoiceListeners(voice) {
		voice.addEventListener('durationchange', this.handleDurationChange);
		voice.addEventListener('pause', this.handlePause);
		voice.addEventListener('play', this.handlePlay);
	}

	detachVoiceListeners(voice) {
		if (!voice) return;
		voice.removeEventListener('durationchange', this.handleDurationChange);
		voice.removeEventListener('pause', this.handlePause);
		voice.removeEventListener('play', this.handlePlay);
	}

	handleDurationChange() {
		const duration = this.voice.duration;

		if (duration) {
			this.setState({
				currentTime: duration,
				duration,
			});
		}
	}

	tick() {
		const voice = this.voice;
		const hasStoppedPlaying = voice.ended || voice.paused;
		if (hasStoppedPlaying) {
			this.setState({
				isDone: true,
			});
		} else {
			this.setState({
				currentTime: voice.currentTime,
			});
		}

		if (!this.state.isDone) {
			this.animationFrame = requestAnimationFrame(this.tick);
		}
	}

	completeCallback() {
		if (this.props.onComplete) {
			this.props.onComplete();
		}
	}

	handlePlay() {
		this.disarmAutoplayRetry();

		this.setState(
			{
				isDone: false,
				isPlaying: true,
			},
			() => {
				this.tick();
			}
		);

		if (typeof this.props.onStart === 'function') {
			this.props.onStart();
		}
	}

	handlePause() {
		this.setState({
			isPlaying: false,
		});

		const voice = this.voice;
		const isEnded = voice.ended;

		if (isEnded) {
			this.completeCallback();
		}

		cancelAnimationFrame(this.animationFrame);

		if (typeof this.props.onPause === 'function') {
			this.props.onPause();
		}
	}

	handleStartPlayer() {
		playPause(this.voice, this.props.refs, this.props.onErrorPlay);
	}

	armAutoplayRetry() {
		if (this.isAutoplayRetryArmed || typeof document === 'undefined') {
			return;
		}
		this.isAutoplayRetryArmed = true;
		document.addEventListener('touchend', this.retryAutoplayOnGesture, true);
		document.addEventListener('click', this.retryAutoplayOnGesture, true);
	}

	disarmAutoplayRetry() {
		if (!this.isAutoplayRetryArmed || typeof document === 'undefined') {
			return;
		}
		this.isAutoplayRetryArmed = false;
		document.removeEventListener(
			'touchend',
			this.retryAutoplayOnGesture,
			true
		);
		document.removeEventListener('click', this.retryAutoplayOnGesture, true);
	}

	retryAutoplayOnGesture() {
		this.disarmAutoplayRetry();
		if (this.voice && this.voice.paused) {
			this.handleStartPlayer();
		}
	}

	startAutoplay() {
		if (this.props.autoplay && this.voice && this.voice.paused) {
			this.handleStartPlayer();

			if (isLikelyIPad()) {
				// The shared AudioContext should already be unlocked by the
				// time this runs (see utils/audioEngine's unlockAudioEngine,
				// called from the app's earliest user gesture), so this is
				// only a defense-in-depth net for builds/paths where that
				// didn't happen - arm it immediately and unconditionally
				// rather than only inside a .catch(), since a stalled
				// promise on some WebKit builds would otherwise leave it
				// never armed.
				this.armAutoplayRetry();
			}
		}
	}

	render() {
		const {disabled, inline} = this.props;

		const {currentTime, duration, isPlaying} = this.state;

		let progress = 0;
		if (isPlaying) {
			const percentageDone = (currentTime * 100) / duration;
			progress =
				DASHARRAY_LENGTH - (percentageDone * DASHARRAY_LENGTH) / 100;
		}

		return (
			<button
				className={classNames({
					'lit-circle': true,
					'lit-audio': true,
					'is-playing': isPlaying,
					inline: inline,
				})}
				disabled={disabled}
				onClick={() => {
					if (!this.voice) {
						return;
					}
					this.handleStartPlayer();
				}}
				title={disabled ? strings.audioMax : null}
			>
				<svg
					className="lit-circle__interactive"
					height="90"
					style={{enableBackground: 'new 0 0 90 90'}}
					version="1.1"
					viewBox="0 0 90 90"
					width="90"
					x="0px"
					xmlSpace="preserve"
					xmlns="http://www.w3.org/2000/svg"
					xmlnsXlink="http://www.w3.org/1999/xlink"
					y="0px"
				>
					<g transform="rotate(-84 45 45)">
						<circle
							className="lit-audio__outline"
							cx="45"
							cy="45"
							fill="none"
							r="37"
							stroke="rgba(0,0,0,0.1)"
							strokeLinecap="round"
							strokeWidth="5"
						/>
						<circle
							className="lit-audio__progress"
							cx="45"
							cy="45"
							fill="none"
							r="37"
							stroke="#fff"
							strokeLinecap="round"
							strokeWidth="6"
							style={{
								strokeDashoffset: `${progress}`,
							}}
						/>
					</g>
				</svg>
				<div className="lit-audio__icon" />
			</button>
		);
	}
}

export default AudioPlayer;
